import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { TableContainer } from "@mui/material";
import { DataTable, TableHead, TableHeadRow, TableHeader } from "@outpave/ds";
import { useQuery } from "@apollo/client";
import SettingPolicyType from "../../../enums/SettingPolicyType";
import { GET_SETTING_POLICIES } from "../../../graphql/settingPolicy";
import LoadingWithoutModal from "../../common/LoadingWithoutModal";
import PolicyTableSection, {
  POLICY_COLUMN_COUNT,
} from "./common/PolicyTableSection";
import {
  APPROVAL_COMBINATIONS,
  ApprovalPolicy,
} from "../../../hooks/settingPolicy.types";

// One colgroup for the whole table is what keeps the three sections aligned;
// the widths used to be restated on every cell in two files.
const APPROVAL_COLUMN_WIDTH = 152;
const ACTIONS_COLUMN_WIDTH = 96;

const transformSettingPolicyType = (type: SettingPolicyType): string => {
  switch (type) {
    case SettingPolicyType.WORKFLOW_APPROVAL:
      return "Cards";
    case SettingPolicyType.INVOICE_APPROVAL:
      return "Invoices";
    case SettingPolicyType.EXPENSE_REIMBURSEMENT:
      return "Expense Reimbursements";
    case SettingPolicyType.PAYMENT_APPROVAL:
      return "Payments";
    default:
      return type;
  }
};

const getDescription = (
  type: SettingPolicyType,
  t: (key: string) => string,
): string => {
  switch (type) {
    case SettingPolicyType.WORKFLOW_APPROVAL:
      return t("WorkflowApprovalDescription");
    case SettingPolicyType.INVOICE_APPROVAL:
      return t("InvoiceApprovalDescription");
    case SettingPolicyType.EXPENSE_REIMBURSEMENT:
      return t("ExpenseReimbursementApprovalDescription");
    case SettingPolicyType.PAYMENT_APPROVAL:
      return t("PaymentApprovalDescription");
    default:
      return t("DefaultApprovalDescription");
  }
};

const settingPolicyTypes = [
  SettingPolicyType.WORKFLOW_APPROVAL,
  SettingPolicyType.EXPENSE_REIMBURSEMENT,
  SettingPolicyType.PAYMENT_APPROVAL,
];

const PoliciesWorkflows = () => {
  const { t } = useTranslation();

  const {
    data: settingPoliciesData,
    refetch: refetchSettingPolicies,
    loading: isLoading,
  } = useQuery(GET_SETTING_POLICIES, {
    variables: {
      settingPolicyTypes,
    },
  });

  const workflowPolicies: ApprovalPolicy[] = useMemo(() => {
    return settingPoliciesData?.mySettingPolicies || [];
  }, [settingPoliciesData]);

  return (
    <>
      <TableContainer sx={{ height: "100%" }}>
        <DataTable stickyHeader>
          <colgroup>
            {Array.from(
              { length: POLICY_COLUMN_COUNT - 1 },
              (_unused, index) => (
                <col key={index} style={{ width: APPROVAL_COLUMN_WIDTH }} />
              ),
            )}
            <col style={{ width: ACTIONS_COLUMN_WIDTH }} />
          </colgroup>

          <TableHeader>
            <TableHeadRow>
              <TableHead>{t("User Role")}</TableHead>
              {APPROVAL_COMBINATIONS.map((combination) => (
                <TableHead key={combination.key} align="center">
                  {t(combination.label)}
                </TableHead>
              ))}
              <TableHead />
            </TableHeadRow>
          </TableHeader>

          {settingPolicyTypes.map((type) => {
            const policies = workflowPolicies.filter(
              (policy) => policy.type === type,
            );
            return (
              <PolicyTableSection
                key={type}
                type={type}
                policies={policies}
                title={t(transformSettingPolicyType(type))}
                tooltipDescription={t("ManagePoliciesTooltip", {
                  policyType: t(transformSettingPolicyType(type)).toLowerCase(),
                })}
                approvalsDrawerDescription={getDescription(type, t)}
                refetch={refetchSettingPolicies}
              />
            );
          })}
        </DataTable>
      </TableContainer>

      <LoadingWithoutModal open={isLoading} />
    </>
  );
};

export default PoliciesWorkflows;
